import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useOS } from "./OSContext";
import { OS_NAV, ENTITY_TO_NAV } from "./nav";
import CommandPalette from "./CommandPalette";
import AttentionPulse from "./AttentionPulse";
import WorkspaceSwitcher from "@/components/nav/WorkspaceSwitcher";
import { Loader2, Search, ChevronDown, Command, Menu, X } from "lucide-react";

export default function OSShell() {
  const { user } = useAuth();
  const { ctx, orgs, orgId, switchOrg, attention } = useOS();
  const location = useLocation();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [orgMenu, setOrgMenu] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (user === null) navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
  }, [user, location.pathname, navigate]);

  // Close the mobile navigator drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (user === undefined || ctx === undefined) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-slate-400 gap-2"><Loader2 className="animate-spin" /> Loading ArcaOS…</div>;
  }
  if (user === null) return null;
  if (ctx === null) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-slate-400">You don’t have ArcaOS access yet.</div>;
  }

  const activeOrg = orgs.find((o) => o.id === orgId);
  const initial = (user.name || user.email || "?").trim()[0]?.toUpperCase() || "?";
  const currentNav = [...OS_NAV].sort((a, b) => b.path.length - a.path.length)
    .find((n) => location.pathname === n.path || (n.path !== "/os" && location.pathname.startsWith(n.path)));
  const currentLabel = location.pathname === "/os" ? "Executive Dashboard" : (currentNav?.label || "Executive Dashboard");

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Mobile backdrop */}
      {navOpen && <div className="fixed inset-0 bg-black/70 z-40 lg:hidden" onClick={() => setNavOpen(false)} data-testid="os-nav-backdrop" />}
      {/* Left Navigator — off-canvas drawer below lg, static sidebar on lg+ */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-white/10 bg-[#070707] flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="os-navigator">
        <div className="flex items-center justify-between border-b border-white/10 pr-2">
          <Link to="/os" className="flex items-center gap-2 px-5 h-16 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#0055FF] flex items-center justify-center font-display font-bold shrink-0">A</div>
            <div className="leading-tight min-w-0"><div className="font-display font-bold truncate">Mission Control</div><div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">ArcaOS</div></div>
          </Link>
          <button className="lg:hidden p-2 text-slate-400 hover:text-white" onClick={() => setNavOpen(false)} data-testid="os-nav-close" aria-label="Close navigation"><X size={18} /></button>
        </div>
        <nav className="flex-1 overflow-auto p-3 space-y-1">
          {OS_NAV.map((n) => {
            const active = location.pathname === n.path || (n.path !== "/os" && location.pathname.startsWith(n.path));
            // Badge count: the Attention item shows total open; module items show their own entity's open count.
            let badge = 0;
            if (n.key === "attention") badge = attention?.total_open || 0;
            else {
              const byType = attention?.by_entity_type || {};
              badge = Object.entries(byType).reduce((sum, [etype, cnt]) => sum + (ENTITY_TO_NAV[etype] === n.key ? cnt : 0), 0);
            }
            return (
              <Link key={n.key} to={n.path} data-testid={`os-nav-${n.key}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00F0FF] ${active ? "bg-[#0055FF]/15 text-white border border-[#0055FF]/30" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
                <n.icon size={16} className={active ? "text-[#00F0FF]" : ""} />
                <span>{n.label}</span>
                {badge > 0 && (
                  <span className={`ml-auto min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${n.key === "attention" ? "bg-rose-500 text-white" : "bg-[#00F0FF]/20 text-[#00F0FF]"}`} data-testid={`os-nav-badge-${n.key}`}>{badge}</span>
                )}
                {badge === 0 && n.phase === 1 && <span className="ml-auto text-[9px] font-mono text-slate-500 uppercase">P1</span>}
              </Link>
            );
          })}
        </nav>
        <Link to="/studio" className="px-5 py-3 border-t border-white/10 text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00F0FF] transition-colors">← ArcaCentum Studio</Link>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-white/10 flex items-center gap-2 sm:gap-3 px-4 lg:px-6">
          <button className="lg:hidden p-2 -ml-2 text-slate-300 hover:text-white shrink-0 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]" onClick={() => setNavOpen(true)} data-testid="os-nav-toggle" aria-label="Open navigation"><Menu size={20} /></button>
          <div className="hidden lg:block"><WorkspaceSwitcher /></div>
          <nav className="hidden xl:flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500" data-testid="os-breadcrumb" aria-label="Breadcrumb">
            <span className="text-slate-400">Mission Control</span>
            <span className="text-slate-500">/</span>
            <span className="text-[#00F0FF]">{currentLabel}</span>
          </nav>
          <button onClick={() => setPaletteOpen(true)} className="ml-auto flex items-center gap-2 text-slate-300 border border-white/10 rounded-xl px-3 py-2 hover:border-white/20 outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] transition-colors lg:w-64 shrink-0" data-testid="os-search-trigger" aria-label="Search or jump to">
            <Search size={15} /> <span className="text-sm hidden lg:inline">Search or jump to…</span>
            <span className="ml-auto hidden lg:flex items-center gap-1 text-[10px] font-mono text-slate-500"><Command size={11} />K</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <AttentionPulse />
            {/* Org switcher */}
            <div className="relative">
              <button onClick={() => setOrgMenu((o) => !o)} className="flex items-center gap-2 border border-white/10 rounded-xl px-2 sm:px-3 py-2 text-sm hover:border-white/20 outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] transition-colors" data-testid="os-org-switcher" aria-label="Switch organization" aria-haspopup="true" aria-expanded={orgMenu}>
                <span className="w-5 h-5 rounded bg-[#00F0FF]/20 text-[#00F0FF] text-[10px] font-bold flex items-center justify-center shrink-0">{(activeOrg?.name || "?")[0]}</span>
                <span className="hidden sm:inline max-w-[8rem] truncate">{activeOrg?.name || "Org"}</span> <ChevronDown size={14} className="shrink-0" />
              </button>
              {orgMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setOrgMenu(false)} />
                  <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#0a0a0a] p-2 z-20" data-testid="os-org-menu">
                    {orgs.map((o) => (
                      <button key={o.id} onClick={() => { switchOrg(o.id); setOrgMenu(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${o.id === orgId ? "text-white bg-white/5" : "text-slate-300 hover:bg-white/5"}`} data-testid={`os-org-${o.id}`}>
                        {o.name}<span className="block text-[10px] font-mono text-slate-500">{(o.my_roles || []).join(", ")}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="w-9 h-9 rounded-full bg-[#0055FF] flex items-center justify-center font-display font-bold" title={user.email} data-testid="os-account">{initial}</div>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
